import { Divider, FormControl, FormHelperText, List } from "@mui/material";
import _ from "lodash";
import { useEffect, useState, type FC } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { isInnerItemEditingAtom, Log, type ICustomField } from "@ryde/shared";

import { GroupTitleTemplate } from "../group_title.template";
import { ContactItem } from "./contact-item";
import { ContactItemAdd } from "./contact-item.add";
import { emptyContact, type IContactItem } from "./table-contacts.models";
import { TableContactsRow, TableContactsStyled } from "./table-contacts.styled";

export const TableContactsTemplate: FC<ICustomField> = ({
  name,
  formLabel,
  formAttributes,
}) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const [editingCount, setEditingCount] = useState(0);
  const setIsInnerItemEditing = useSetRecoilState(isInnerItemEditingAtom);

  const handleEditStart = useEvent(() => {
    setEditingCount(prev => prev + 1);
  });

  const handleEditEnd = useEvent(() => {
    setEditingCount(prev => Math.max(0, prev - 1));
  });

  useEffect(() => {
    setIsInnerItemEditing(editingCount > 0);
  }, [editingCount, setIsInnerItemEditing]);

  useEffect(() => {
    return () => {
      setIsInnerItemEditing(false);
    };
  }, [setIsInnerItemEditing]);

  const contactsList: IContactItem[] | undefined = useWatch({ control, name });

  useEffect(() => {
    if (contactsList && !_.isArray(contactsList)) {
      /* guard for invalid value coming from backend*/
      setValue(name, []);
    }
  }, [contactsList, name, setValue]);

  Log.debug("Contacts list normalized value", contactsList);

  const removeHandler = useEvent((index: number) => {
    Log.debug("Remove contact handler fired for index ", index);
    contactsList?.splice(index, 1);
    setValue(name, contactsList || [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  });

  return (
    <TableContactsStyled>
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => {
          return (
            <FormControl
              error={!!error}
              data-e2e-template="table-contacts-template"
            >
              <GroupTitleTemplate
                formLabel={formLabel}
                name={formLabel + "_label"}
                formAttributes={formAttributes}
              />
              <div
                className="table-contacts__container"
                data-inner-editing={editingCount > 0 ? "true" : undefined}
              >
                <List sx={{ width: "100%", bgcolor: "background.paper" }}>
                  {!!contactsList?.length &&
                    contactsList?.map((contactField, index) => {
                      const listField = contactField?.name
                        ? contactField
                        : emptyContact;
                      const uniqueKeyValue = `${listField.name}_${listField.phone}_${listField.email}`;

                      return (
                        <TableContactsRow
                          data-e2e-value={uniqueKeyValue}
                          key={uniqueKeyValue}
                        >
                          <ContactItem
                            fieldName={name}
                            control={control}
                            index={index}
                            setValue={setValue}
                            value={listField || emptyContact}
                            removeHandler={removeHandler}
                            onEditStart={handleEditStart}
                            onEditEnd={handleEditEnd}
                          />
                          <Divider
                            variant="inset"
                            component="li"
                            sx={{ my: "10px" }}
                          />
                        </TableContactsRow>
                      );
                    })}
                  <ContactItemAdd
                    fieldName={name}
                    index={contactsList?.length || 0}
                    setValue={setValue}
                    onEditStart={handleEditStart}
                    onEditEnd={handleEditEnd}
                  />
                </List>
              </div>

              {error && (
                <FormHelperText>
                  {error.message || t("errors.invalidContactListFormat")}
                </FormHelperText>
              )}
            </FormControl>
          );
        }}
      />
    </TableContactsStyled>
  );
};
